import { lazy, Suspense, useCallback, useMemo } from 'react'
import { com } from '@eidu/web'
import MicrofrontendErrorBoundary from './MicrofrontendErrorBoundary'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import { useModifyEntities } from '../../io/useEntityMutations'
import type Patch from '../../io/api/entity/data/Patch'
import entityIdFromString = com.eidu.entity.entityIdFromString
import linkKeyFromString = com.eidu.entity.link.linkKeyFromString

const ENTITY_LINK_KEY_LEARNING_UNIT_ID = 'com.eidu.dpl.learningunitid'

type SelectableUnit = {
  entityId: string
  name: string
  entityLinks: Array<{ key: unknown; value: string | null }>
}

export type BatchPublisherWrapperProps = {
  selectedEntities: readonly EntityWithLabelAndRelated[]
}

const BatchPublisher = lazy(
  // @ts-ignore -- resolved at runtime via module federation
  async () => import('eidu-remotes/dpl/batch-publisher')
)

const toSelectableUnit = (entity: EntityWithLabelAndRelated): SelectableUnit => ({
  entityId: entity.entity.id.toString(),
  name: entity.primaryLabelText,
  entityLinks: [...entity.entity.links.asJsReadonlyMapView().entries()].map(([key, value]) => ({
    key,
    value,
  })),
})

const BatchPublisherWrapper = ({ selectedEntities }: BatchPublisherWrapperProps) => {
  const selectedUnits = useMemo(() => selectedEntities.map(toSelectableUnit), [selectedEntities])
  const { mutateAsync: modifyEntities } = useModifyEntities()

  const handleUnitPublished = useCallback(
    async (entityId: string, curriculumUnitId: string) => {
      const patch: Patch = {
        valuesToUpdateByFieldId: new Map(),
        linksToUpdate: new Map([[linkKeyFromString(ENTITY_LINK_KEY_LEARNING_UNIT_ID), curriculumUnitId]]),
        externalValuesToUpdate: new Map(),
      }
      await modifyEntities(new Map([[entityIdFromString(entityId), patch]]))
    },
    [modifyEntities]
  )

  return (
    <MicrofrontendErrorBoundary fallback={null}>
      <Suspense fallback={null}>
        <BatchPublisher selectedUnits={selectedUnits} onUnitPublished={handleUnitPublished} />
      </Suspense>
    </MicrofrontendErrorBoundary>
  )
}

export default BatchPublisherWrapper
